import React, { useEffect, useMemo, useRef } from "react";
import classNames from "classnames";
import { ClassFilterType } from "@common/types/class";
import { FiltersTypes } from "@common/types/filters/general";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { Card } from "@design-system/Card/Card";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./TablePageHeader.module.scss";
import { FiltersChangeFn, FilterTabs } from "./filters/lib";
import { Filters, FiltersProps } from "./filters/Filters";
import { TABLE_PAGE_HEADER_DEFAULT_SEARCH_MIN_LENGTH } from "./tablePageHeaderSearch";

interface Props extends Omit<
    FiltersProps,
    "filters" | "onChange" | "availableFilters"
> {
    children?: React.ReactNode;
    className?: string;

    prependChildren?: React.ReactNode;
    staticActiveFilters?: React.ReactNode;
    beforeFiltersChildren?: React.ReactNode;
    afterFiltersChildren?: React.ReactNode;

    search?: string;
    searchPlaceholder?: string;
    onSearchChange?: (val: string) => void;
    searchKeyboardShortcuts?: boolean;
    hasSearchFindShortcut?: boolean;
    searchMinLength?: number;
    searchDebounceTime?: number;

    filters?: FiltersTypes;
    onFiltersChange?: FiltersChangeFn;
    availableFilters?: FilterTabs[];
    hardcodedEntityId?: number;

    hideOverlay?: boolean;
    hideClearButton?: boolean;

    isFullPage?: boolean;
    borderTop?: boolean;
    borderBottom?: boolean;
    tabNavigationTop?: React.ReactNode;
    tabNavigationBottom?: React.ReactNode;
    filterContainerClassName?: string;
}

export const TablePageHeader: React.FC<Props> = ({
    children,
    prependChildren,
    staticActiveFilters,
    beforeFiltersChildren,
    afterFiltersChildren,
    search,
    searchPlaceholder = "Search...",
    onSearchChange,
    searchKeyboardShortcuts = true,
    hasSearchFindShortcut = false,
    searchMinLength = TABLE_PAGE_HEADER_DEFAULT_SEARCH_MIN_LENGTH,
    searchDebounceTime,
    className,
    filterContainerClassName,
    filters,
    onFiltersChange,

    availableFilters,
    defaultFilters,
    defaultTab = availableFilters?.[0],
    directionSwitchDisabled,
    directionSwitchReadonly,
    directionRadios,
    prioritizedActiveFilters,
    disabledActiveFilters,
    hardcodedEntityId,
    hideOverlay,
    hideClearButton,
    availableClassifications,
    staticAccountTypeNodes,
    staticTemplateGroupOptions,
    accountFilterSourceTypes,
    accountFilterPredicate,
    onlyStandardCategories,
    shouldShowNoAccountOption,
    shouldShowKickAccountOption,
    hideCoaAccountLastChangedBy,
    valuePickersOnly,
    disabled,
    isFullPage,
    borderTop,
    borderBottom,
    tabNavigationTop,
    tabNavigationBottom,
    showAmountFilterLevel,
    counterpartyEntityLabel,
    counterpartyDirection,
    counterpartySingleSelect,
    counterpartyInvoicesOnly,
    hasTaskCreatedByClientOption,
    hasTaskAssigneeOrgMembersOnly,
    hasTaskAssignedToClientOption,
    ledgerFilterLabel,
    staticHoldingAccountOptions,
    filterButtonLabel,
    tabLabels,
    draftStatusOptions,
    draftSourceOptions,
    filtersContainerRef: externalFiltersContainerRef,
}) => {
    const ownFiltersContainerRef = useRef<HTMLDivElement>(null);
    const filtersContainerRef =
        externalFiltersContainerRef ?? ownFiltersContainerRef;
    const isClassesEnabled = useIsClassesEnabled();
    const gatedAvailableFilters = useMemo(
        () =>
            isClassesEnabled
                ? availableFilters
                : availableFilters?.filter(
                      (filterTab) => filterTab !== FilterTabs.CLASS,
                  ),
        [availableFilters, isClassesEnabled],
    );
    const resolvedDefaultTab =
        !isClassesEnabled && defaultTab === FilterTabs.CLASS
            ? (gatedAvailableFilters?.[0] ?? FilterTabs.DATE)
            : defaultTab;

    useEffect(() => {
        function clearClassFilterWithoutPlanAccess() {
            if (isClassesEnabled || !onFiltersChange || !filters) {
                return;
            }
            const classFilterIsActive =
                !!filters.classes ||
                filters.classFilterType === ClassFilterType.NO_CLASSES ||
                filters.classFilterType === ClassFilterType.HAS_CLASSES;
            if (!classFilterIsActive) {
                return;
            }
            onFiltersChange({
                classes: undefined,
                classFilterType: ClassFilterType.ANY_SELECTED,
            });
        }
        clearClassFilterWithoutPlanAccess();
    }, [filters, isClassesEnabled, onFiltersChange]);

    return (
        <Card
            className={classNames(
                styles.container,
                {
                    [styles.fullPage]: isFullPage,
                    [styles.borderTop]: borderTop,
                    [styles.borderBottom]: borderBottom,
                },
                className,
            )}
        >
            <VStack className={classNames("w-100", styles.content)}>
                {tabNavigationTop && (
                    <div className={styles.navContainerTop}>
                        {tabNavigationTop}
                    </div>
                )}
                <HStack
                    justify="between"
                    gap={8}
                    className={classNames(
                        styles.filterContainer,
                        filterContainerClassName,
                    )}
                >
                    <div ref={filtersContainerRef} className="flex-grow-1">
                        <HStack gap={8} className="w-100">
                            {prependChildren}
                            {onSearchChange && (
                                <SearchFilter
                                    placeholder={searchPlaceholder}
                                    onChange={(value) =>
                                        onSearchChange(value ?? "")
                                    }
                                    filter={search}
                                    minLength={searchMinLength}
                                    debounceTime={searchDebounceTime}
                                    keyboardShortcuts={searchKeyboardShortcuts}
                                    hasFindShortcut={hasSearchFindShortcut}
                                    disabled={disabled}
                                />
                            )}

                            {beforeFiltersChildren}

                            {onFiltersChange &&
                                !!gatedAvailableFilters?.length && (
                                    <Filters
                                        onChange={onFiltersChange}
                                        filters={filters ?? {}}
                                        availableFilters={gatedAvailableFilters}
                                        defaultFilters={defaultFilters}
                                        defaultTab={resolvedDefaultTab}
                                        directionSwitchDisabled={
                                            directionSwitchDisabled
                                        }
                                        directionSwitchReadonly={
                                            directionSwitchReadonly
                                        }
                                        directionRadios={directionRadios}
                                        prioritizedActiveFilters={
                                            prioritizedActiveFilters
                                        }
                                        disabledActiveFilters={
                                            disabledActiveFilters
                                        }
                                        hardcodedEntityId={hardcodedEntityId}
                                        hideOverlay={hideOverlay}
                                        hideClearButton={hideClearButton}
                                        staticActiveFilters={
                                            staticActiveFilters
                                        }
                                        availableClassifications={
                                            availableClassifications
                                        }
                                        staticAccountTypeNodes={
                                            staticAccountTypeNodes
                                        }
                                        staticTemplateGroupOptions={
                                            staticTemplateGroupOptions
                                        }
                                        staticHoldingAccountOptions={
                                            staticHoldingAccountOptions
                                        }
                                        filterButtonLabel={filterButtonLabel}
                                        tabLabels={tabLabels}
                                        draftStatusOptions={draftStatusOptions}
                                        draftSourceOptions={draftSourceOptions}
                                        accountFilterSourceTypes={
                                            accountFilterSourceTypes
                                        }
                                        accountFilterPredicate={
                                            accountFilterPredicate
                                        }
                                        onlyStandardCategories={
                                            onlyStandardCategories
                                        }
                                        shouldShowNoAccountOption={
                                            shouldShowNoAccountOption
                                        }
                                        shouldShowKickAccountOption={
                                            shouldShowKickAccountOption
                                        }
                                        hideCoaAccountLastChangedBy={
                                            hideCoaAccountLastChangedBy
                                        }
                                        valuePickersOnly={valuePickersOnly}
                                        showAmountFilterLevel={
                                            showAmountFilterLevel
                                        }
                                        disabled={disabled}
                                        filtersContainerRef={
                                            filtersContainerRef
                                        }
                                        counterpartyEntityLabel={
                                            counterpartyEntityLabel
                                        }
                                        counterpartyDirection={
                                            counterpartyDirection
                                        }
                                        counterpartySingleSelect={
                                            counterpartySingleSelect
                                        }
                                        counterpartyInvoicesOnly={
                                            counterpartyInvoicesOnly
                                        }
                                        hasTaskCreatedByClientOption={
                                            hasTaskCreatedByClientOption
                                        }
                                        hasTaskAssigneeOrgMembersOnly={
                                            hasTaskAssigneeOrgMembersOnly
                                        }
                                        hasTaskAssignedToClientOption={
                                            hasTaskAssignedToClientOption
                                        }
                                        ledgerFilterLabel={ledgerFilterLabel}
                                    />
                                )}

                            {afterFiltersChildren}
                        </HStack>
                    </div>

                    <HStack gap={8}>{children}</HStack>
                </HStack>
                {tabNavigationBottom && (
                    <div className={styles.navContainerBottom}>
                        {tabNavigationBottom}
                    </div>
                )}
            </VStack>
        </Card>
    );
};
